'use client';

const STORAGE_KEY = 'auth-gate-pending-action';

const CLAIMED_SIGNAL_KEY = 'auth-gate-resumed';

const MAX_AGE_MS = 30 * 60 * 1000;

export type PendingGateAction = {
  type: 'trialDownload';
  sku: string;
  isDownload: boolean;
};

type StoredPendingGateAction = PendingGateAction & {
  pathname: string;
  createdAt: number;
  method?: string;
};

const isStored = (value: unknown): value is StoredPendingGateAction => {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as StoredPendingGateAction;
  // localStorage is shared with everything on the origin: check field types before reading them.
  return (
    candidate.type === 'trialDownload' &&
    typeof candidate.sku === 'string' &&
    candidate.sku.length > 0 &&
    typeof candidate.isDownload === 'boolean' &&
    typeof candidate.pathname === 'string' &&
    candidate.pathname.length > 0 &&
    typeof candidate.createdAt === 'number' &&
    Number.isFinite(candidate.createdAt)
  );
};

export const persistPendingGateAction = (action: PendingGateAction): boolean => {
  try {
    const stored: StoredPendingGateAction = {
      ...action,
      pathname: window.location.pathname,
      createdAt: Date.now(),
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    return true;
  } catch {
    return false;
  }
};

const readStored = (): StoredPendingGateAction | null => {
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isStored(parsed)) return null;

  if (parsed.pathname !== window.location.pathname) return null;
  if (Date.now() - parsed.createdAt > MAX_AGE_MS) return null;

  return parsed;
};

export const readPendingGateAction = (): PendingGateAction | null => {
  const stored = readStored();
  if (!stored) return null;

  return { type: stored.type, sku: stored.sku, isDownload: stored.isDownload };
};

export const persistPendingGateMethod = (method: string): void => {
  const stored = readStored();
  if (!stored) return;

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...stored, method }));
  } catch {
    // Analytics only; must never break the resume.
  }
};

export type PendingGateClaim =
  | { status: 'claimed'; action: PendingGateAction; method: string | null }
  | { status: 'stale' }
  | { status: 'gone' };

const announceClaim = (): void => {
  try {
    const token = `${Date.now()}.${Math.random().toString(36).slice(2, 8)}`;
    window.localStorage.setItem(CLAIMED_SIGNAL_KEY, token);
  } catch {
    // Without the signal a gate open elsewhere stays up; the claim already prevents a double download.
  }
};

export const onPendingGateActionClaimed = (listener: () => void): (() => void) => {
  const handleStorage = (event: StorageEvent) => {
    // A removal is not a claim: `clearPendingGateAction` runs on every close, everywhere.
    if (event.key !== CLAIMED_SIGNAL_KEY || event.newValue === null) return;
    listener();
  };

  window.addEventListener('storage', handleStorage);
  return () => window.removeEventListener('storage', handleStorage);
};

const hasStoredEntry = (): boolean => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) !== null;
  } catch {
    return false;
  }
};

export const claimPendingGateAction = (): PendingGateClaim => {
  const stored = readStored();
  if (!stored) {
    return hasStoredEntry() ? { status: 'stale' } : { status: 'gone' };
  }

  clearPendingGateAction();
  announceClaim();
  return {
    status: 'claimed',
    action: { type: stored.type, sku: stored.sku, isDownload: stored.isDownload },
    method: stored.method ?? null,
  };
};

export const clearPendingGateAction = (): void => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable; nothing to clean.
  }
};
